import { Box } from '@mui/material';
import React, { ReactNode, useMemo, useEffect } from 'react';
import DialogMediaPlayer from '../../../../../DialogMediaPlayer/DialogMediaPlayer';
import { ReactPlayerProps } from 'react-player';
import usePractitionerControlsContext from '../../../../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import Flat from '../../../../../../assets/icons/Flat.svg?react';
import Union from '../../../../../../assets/icons/Union.svg?react';
import { VideoContentPanelStyle } from './VideoContentPanelStyle';
import useAppContext from '../../../../../../hooks/useAppContext/useAppContext';
import CombineToggle from '../../../../../UI/CombineToggle/CombineToggle';
import { SourceProjection } from '../../../../../../commands/practitioner/enums/SourceProjection';
import { useTranslation } from 'react-i18next';
import DefaultVideo from '../../../../../../assets/no-video.svg';
import { PLayerTriggeredBy } from '../../../../../../commands/practitioner/enums/PLayerTriggeredBy';
import { MediaTypes } from '../../../../../../commands/practitioner/enums/MediaTypes';

interface projectionTypeInterface {
  type: 'text';
  content: '2D/Flat' | '3D/360';
  startIcon?: ReactNode;
  isActive: boolean;
}

interface VideoContentPanelProps {
  className?: string;
  index: number;
  value: number;
}

const VideoContentPanel = (props: VideoContentPanelProps) => {
  const { index, value } = props;

  const {
    practitionerMediaURI,
    projectionType,
    setProjectionType,
    isMediaLoaded,
    practitionerMediaType,
  } = usePractitionerControlsContext();
  const { hasDevice } = useAppContext();
  const { t } = useTranslation();
  const showCompbineSwitcher =
    practitionerMediaType === MediaTypes.VIDEO && !!practitionerMediaURI && isMediaLoaded && hasDevice;
  const { videoTabContainer } = VideoContentPanelStyle;

  const config = {
    youtube: {
      playerVars: {
        showinfo: 1,
        origin: window.location.origin,
      },
    },
  };

  const reactPlayerPreview: ReactPlayerProps = {
    url: practitionerMediaURI,
    width: '100%',
    height: '100%',
    config,
    controls: false,
  };

  useEffect(() => {
    if (value !== MediaTypes.VIDEO) {
      setProjectionType(SourceProjection.Flat);
    }
  }, [value]);

  const onProjectionChange = (): void => {
    if (projectionType === SourceProjection.Flat) {
      setProjectionType(SourceProjection.EAC);
    } else if (projectionType === SourceProjection.EAC) {
      setProjectionType(SourceProjection.Flat);
    }
  };

  const projectionTypeOptions: projectionTypeInterface[] = useMemo(
    () => [
      {
        type: 'text',
        content: t('projection-flat'),
        startIcon: <Flat />,
        isActive: projectionType === SourceProjection.Flat ? true : false,
      },
      {
        type: 'text',
        content: '3D/360',
        startIcon: <Union />,
        isActive: projectionType === SourceProjection.Flat ? false : true,
      },
    ],
    [projectionType, t]
  );

  return (
    <Box
      sx={videoTabContainer}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <>
          {showCompbineSwitcher && (
            <CombineToggle
              className={'video-type-switch'}
              selectValue={projectionType === SourceProjection.Flat ? true : false}
              options={projectionTypeOptions}
              onChangeHandler={onProjectionChange}
              title={'media-modal-content-type'}
            />
          )}
          <Box className={'video-container'}>
            {!!practitionerMediaURI && isMediaLoaded ? (
              <DialogMediaPlayer
                key={practitionerMediaURI}
                ReactPlayerProps={reactPlayerPreview}
                ReactPlayerTriggeredBy={PLayerTriggeredBy.Practitioner}
              />
            ) : (
              <Box className="video-default">
                <img className={'img'} src={DefaultVideo} alt="blanc-img" />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default VideoContentPanel;
